import React, { useEffect, useState } from "react";
import { Box, Button, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import LabeledTextArea from "../../Widgets/LabeledTextArea";
import LabeledSelect from "../../Widgets/LabeledSelect";
import DateOfActivityPicker from "./DateOfActivityPicker";

const DEGREE_OF_INDEPENDENCE_OPTIONS = [
  { value: "INDEPENDENT", label: "Independent" },
  { value: "PARTIALLY_SUPPORTED", label: "Partially supported" },
  { value: "FULLY_SUPPORTED", label: "Fully supported" },
];

interface ActivityFormValues {
  activityName: string;
  startDate: Date | null;
  endDate: Date | null;
  levelOfParticipation: string;
}

const INITIAL_FORM: ActivityFormValues = {
  activityName: "",
  startDate: null,
  endDate: null,
  levelOfParticipation: "",
};

interface ActivityModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (values: ActivityFormValues) => void;
  isSaving?: boolean;
  initialValues?: Partial<ActivityFormValues>;
}

export const ActivityModal: React.FC<ActivityModalProps> = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  initialValues,
}) => {
  const [formValues, setFormValues] = useState<ActivityFormValues>({
    ...INITIAL_FORM,
    ...initialValues,
  });
  const [noEndDate, setNoEndDate] = useState<boolean>(
    Boolean(initialValues?.startDate && !initialValues?.endDate)
  );

  useEffect(() => {
    if (open) {
      setFormValues({ ...INITIAL_FORM, ...initialValues });
      setNoEndDate(Boolean(initialValues?.startDate && !initialValues?.endDate));
    }
  }, [open]);

  const handleChange =
    (field: keyof ActivityFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleDateRangeChange = ([start, end]: [Date | null, Date | null]) => {
    setFormValues((prev) => ({ ...prev, startDate: start, endDate: end }));
  };

  const handleNoEndDateChange = (checked: boolean) => {
    setNoEndDate(checked);
    if (checked) {
      setFormValues((prev) => ({ ...prev, endDate: null }));
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    if (onSave) {
      onSave(formValues);
    } else {
      handleClose();
    }
  };

  const isEditMode = Boolean(initialValues && Object.keys(initialValues).length > 0);

  const isSaveDisabled =
    !formValues.activityName.trim() ||
    !formValues.startDate ||
    !formValues.levelOfParticipation ||
    (!noEndDate && !formValues.endDate);

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Edit Activity" : "Add Activity"}
      maxWidth="xs"
      drawerContentSx={{ maxHeight: "calc(80vh - 160px)" }}
    >
      <Box sx={{ p: 0 }}>
        <Grid container spacing={7} sx={{ p: "24px 0px" }}>
          <LabeledTextArea
            label="Activity Name"
            value={formValues.activityName}
            onChange={handleChange("activityName")}
            multiline={false}
          />
          <DateOfActivityPicker
            startDate={formValues.startDate}
            endDate={formValues.endDate}
            onDatesChange={handleDateRangeChange}
            noEndDate={noEndDate}
            onNoEndDateChange={handleNoEndDateChange}
          />
          <LabeledSelect
            label="Level of Participation"
            value={formValues.levelOfParticipation}
            onChange={handleChange("levelOfParticipation")}
            options={DEGREE_OF_INDEPENDENCE_OPTIONS}
          />
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mt: "16px" }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            size="small"
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaveDisabled}
          >
            {isEditMode ? "Save Changes" : "Add Activity"}
          </LoadingButton>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default ActivityModal;
